//@flow
import * as React from 'react';
import { Box, Position, Icon, Tooltip } from '@locus-taxy/neo-ui';
import HoveredCollapsibleList from './HoveredCollapsibleLists';
import ItemCard from './ItemCard';
import { TerritoryContext } from './ManualTerritoryProvider';
import { useTranslation } from 'react-i18next';

const OutletsList = (): React.Node => {
    const [visible, setVisible] = React.useState(true);
    const getKey = React.useCallback(({ id }) => id, []);
    const { t } = useTranslation('entityManagement');
    const {
        selectedOutletId,
        onOutletSelect,
        selectedOutlets,
        unallocatedOutlets,
        toggleAddOutletsModel,
        selectedTerritoryId,
        hoveredOutletId,
        layerColorMap,
        onOutletHover
    } = React.useContext(TerritoryContext);

    const setHoveredEntity = React.useCallback(
        id => {
            onOutletHover(id);
        },
        [onOutletHover]
    );
    const renderItem = React.useCallback(
        itemProps => {
            const isOutletUnallocated = unallocatedOutlets.find(outlet => {
                return outlet.id === itemProps.id;
            });
            if (selectedTerritoryId && !isOutletUnallocated) {
                const cardColor = layerColorMap?.get(selectedTerritoryId);
                return (
                    <ItemCard
                        setHoveredEntity={setHoveredEntity}
                        cardColor={cardColor}
                        entity={itemProps}
                        selected={selectedOutletId === itemProps.id}
                        hovered={hoveredOutletId === itemProps.id}
                        onItemClick={onOutletSelect}
                        subHeadingLabel={t('fpPlanView.outletId')}
                    />
                );
            } else {
                return (
                    <ItemCard
                        setHoveredEntity={setHoveredEntity}
                        cardColor="red.200"
                        entity={itemProps}
                        selected={selectedOutletId === itemProps.id}
                        hovered={hoveredOutletId === itemProps.id}
                        onItemClick={onOutletSelect}
                        subHeadingLabel={t('fpPlanView.outletId')}
                    />
                );
            }
        },
        [
            onOutletSelect,
            selectedOutletId,
            hoveredOutletId,
            layerColorMap,
            selectedTerritoryId,
            setHoveredEntity,
            unallocatedOutlets,
            t
        ]
    );

    const hasUnallocatedOutlets = !!unallocatedOutlets.size;

    const setOutletsVisibility = React.useCallback(() => {
        setVisible(isVisible => !isVisible);
    }, []);

    return (
        <Position
            height="calc(100vh - 64px)"
            maxWidth="270px"
            position="relative"
        >
            {visible && (
                <HoveredCollapsibleList
                    title={t('entities:outlet_plural')}
                    list={selectedOutlets}
                    renderItem={renderItem}
                    keyExtractor={getKey}
                    overflowContainerHeight={
                        hasUnallocatedOutlets
                            ? 'calc(50% - 32px - 48px)'
                            : undefined
                    }
                />
            )}
            {visible && hasUnallocatedOutlets && (
                <HoveredCollapsibleList
                    title={t('createTerritory.unallocatedOutlets')}
                    list={unallocatedOutlets}
                    renderItem={renderItem}
                    keyExtractor={getKey}
                    onAddClick={toggleAddOutletsModel}
                    overflowContainerHeight="calc(50% - 32px - 48px)"
                />
            )}

            <Position
                position="absolute"
                right={-6}
                top={0}
                bottom={0}
                zIndex={9}
                display="flex"
                justifyContent="center"
                alignItems="center"
                px={1}
                sx={{
                    cursor: 'col-resize',
                    '&:hover': {
                        '.collapse__line': {
                            opacity: 1,
                            bg: 'blue.500'
                        },
                        '.collapse__button': {
                            visibility: 'visible'
                        }
                    }
                }}
                onClick={setOutletsVisibility}
            >
                <Box
                    className="collapse__line"
                    width="3px"
                    height="100%"
                    bg="blue.300"
                    opacity={0.7}
                    sx={{
                        transition:
                            'opacity ease-in-out 200ms, background ease-in-out 300ms'
                    }}
                />

                <Position
                    className="collapse__button"
                    width={22}
                    height={22}
                    borderRadius={40}
                    bg="white"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    position="absolute"
                    pt="3px"
                    sx={{
                        visibility: 'hidden',
                        transition: 'visibility 300ms, background 300ms',
                        cursor: 'pointer',
                        '&:hover': {
                            '.collapse__icon': {
                                transition: 'color 300ms',
                                color: 'white'
                            },
                            bg: 'blue.500',
                            boxShadow: 'hovered'
                        }
                    }}
                    boxShadow="active"
                >
                    <Tooltip
                        tooltip={
                            visible
                                ? t('fpPlanView.hideOutlets')
                                : t('fpPlanView.showOutlets')
                        }
                        placement="right"
                        shouldWrapChildren
                    >
                        <Icon
                            icon={
                                visible
                                    ? 'arrow_chevron_left'
                                    : 'arrow_chevron_right'
                            }
                            size="medium"
                            color="grey.300"
                            className="collapse__icon"
                        />
                    </Tooltip>
                </Position>
            </Position>
        </Position>
    );
};

export default React.memo(OutletsList);
